import { useEffect, useRef, useState } from "react";

/**
 * useLiveDecisions
 * Opens a WebSocket to /api/ws/decisions?token=<jwt>, receives {type:'decision', decision}
 * messages, and exposes:
 *   - status: 'connecting' | 'live' | 'reconnecting' | 'offline'
 *   - decisions: newest-first, capped
 *   - lastAt: ISO timestamp of most recent message
 *
 * Auto-reconnect with exponential backoff (max 15s). Sends heartbeat ping every 25s.
 */
export default function useLiveDecisions({ enabled = true, max = 30 } = {}) {
  const [status, setStatus] = useState("offline");
  const [decisions, setDecisions] = useState([]);
  const [lastAt, setLastAt] = useState(null);
  const [subscribers, setSubscribers] = useState(0);
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const pingRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const buildUrl = () => {
      const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const wsBase = base.replace(/^http/, "ws");
      const token = localStorage.getItem("sentry_token") || "";
      return `${wsBase}/api/ws/decisions?token=${encodeURIComponent(token)}`;
    };

    const connect = () => {
      if (cancelled) return;
      setStatus(retryRef.current === 0 ? "connecting" : "reconnecting");
      let ws;
      try {
        ws = new WebSocket(buildUrl());
      } catch {
        scheduleRetry();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelled) return;
        retryRef.current = 0;
        setStatus("live");
        // Heartbeat
        pingRef.current = setInterval(() => {
          try { ws.readyState === 1 && ws.send("ping"); } catch { /* noop */ }
        }, 25000);
      };

      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "decision" && msg.decision) {
            setDecisions((prev) => [msg.decision, ...prev].slice(0, max));
            setLastAt(msg.decision.timestamp || new Date().toISOString());
          } else if (msg.type === "hello") {
            setSubscribers(msg.subscribers || 1);
          }
        } catch { /* ignore malformed */ }
      };

      ws.onerror = () => { /* onclose will handle reconnect */ };
      ws.onclose = () => {
        clearInterval(pingRef.current);
        pingRef.current = null;
        if (cancelled) return;
        scheduleRetry();
      };
    };

    const scheduleRetry = () => {
      const backoff = Math.min(15000, 1000 * 2 ** retryRef.current);
      retryRef.current += 1;
      setStatus("reconnecting");
      setTimeout(connect, backoff);
    };

    connect();

    return () => {
      cancelled = true;
      clearInterval(pingRef.current);
      pingRef.current = null;
      try { wsRef.current && wsRef.current.close(); } catch { /* noop */ }
      wsRef.current = null;
    };
  }, [enabled, max]);

  return { status, decisions, lastAt, subscribers };
}
